import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtSessionDate, LangSwitch, LogoHeader, PROV, provSetToken, provToken, StickyNav, useProvLang } from "./provShared";

const GEAR_LABELS = { gloves: "Boxing gloves", wraps: "Hand wraps", towel: "Towel", water: "Water bottle", groin: "Groin protector", bra: "Sports bra", mouthguard: "Mouthguard" };
const WEEKDAYS_EN = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const MSTATUS = { approved: ["APPROVED", "#7BAF7B"], pending: ["PENDING", "#D4A93A"], provisional: ["PROVISIONAL", "#BEBEBE"], rejected: ["REJECTED", "#666666"] };

// PROVOCATIVE. ADMIN — operationele bedrijfs-omgeving (Fase 03).
// Donkergrijze achtergrond (#161616, iets lichter dan de near-black site).
// Tabs: OVERVIEW · MEMBERS · SESSIONS · ATTENDANCE · EQUIPMENT · TEAM ACCESS · MEDIA · SETTINGS.
// Financiële functies (facturatie/betalingen) bestaan hier BEWUST niet — die lopen
// uitsluitend via de centrale JOH Admin → Bedrijven → PROVOKE → Facturatie.
// Rollen: central/admin/beheer (volledig; team-beheer alleen central+admin) · kijken (alleen lezen, geen equipment-details).

// Donkergrijze admin-achtergrond (user-keuze: alleen PROVOCATIVE. Admin)
const ABG = "#161616";
const CARD = "#101010";

function adminAuth() {
  const t = localStorage.getItem("joh_admin_token") || provToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

const wdIndex = (dateStr) => { const d = new Date(`${dateStr}T00:00:00`).getDay(); return d === 0 ? 6 : d - 1; };

export default function ProvAdmin() {
  const nav = useNavigate();
  const { lang, setLang, t } = useProvLang();
  const [role, setRole] = useState(null);
  const [denied, setDenied] = useState(false);
  const [hasToken, setHasToken] = useState(() => !!(localStorage.getItem("joh_admin_token") || provToken()));
  const [aForm, setAForm] = useState({ email: "", password: "" }); // aparte admin-login
  const [aErr, setAErr] = useState("");
  const [aBusy, setABusy] = useState(false);
  const [phases, setPhases] = useState(null);
  const [reportDrafts, setReportDrafts] = useState({});
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [members, setMembers] = useState(null);
  const [mFilter, setMFilter] = useState("all");
  const [sessions, setSessions] = useState(null);
  const [open, setOpen] = useState(null); // geopende sessie (roster)
  const [roster, setRoster] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [edit, setEdit] = useState(null);
  const [adding, setAdding] = useState(false);
  const [walkin, setWalkin] = useState(false);
  const [wq, setWq] = useState("");
  const [wResults, setWResults] = useState([]);
  const [wNew, setWNew] = useState({ first_name: "", last_name: "", email: "" });
  const [equipment, setEquipment] = useState(null);
  const [team, setTeam] = useState(null);
  const [tInvite, setTInvite] = useState({ email: "", role: "beheer" });
  const [media, setMedia] = useState(null);
  const [upProg, setUpProg] = useState({}); // upload-voortgang per media-slot
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const searchTimer = useRef(null);

  const readOnly = role === "kijken";
  const canManageTeam = role === "central" || role === "admin";

  const loadSessions = () => {
    api.get("/provocative/admin/sessions", adminAuth()).then(({ data }) => setSessions(data.items))
      .catch(() => toast.error("Laden mislukt"));
  };

  const loadTab = (tb) => {
    setOpen(null);
    if (tb === "overview") api.get("/provocative/admin/overview", adminAuth()).then(({ data }) => setOverview(data)).catch(() => toast.error("Laden mislukt"));
    if (tb === "members") api.get("/provocative/admin/members", adminAuth()).then(({ data }) => setMembers(data.items)).catch(() => toast.error("Laden mislukt"));
    if (tb === "sessions" || tb === "attendance") loadSessions();
    if (tb === "equipment") api.get("/provocative/admin/equipment", adminAuth()).then(({ data }) => setEquipment(data.items)).catch(() => toast.error("Laden mislukt"));
    if (tb === "team") api.get("/provocative/admin/team", adminAuth()).then(({ data }) => setTeam(data)).catch(() => toast.error("Laden mislukt"));
    if (tb === "media") api.get("/provocative/admin/media", adminAuth()).then(({ data }) => setMedia(data)).catch(() => toast.error("Laden mislukt"));
    if (tb === "settings") api.get("/provocative/admin/settings", adminAuth()).then(({ data }) => setSettings({ days: data.schedule.days.map((d) => ({ ...d })), location: data.schedule.location, enabled: data.enabled })).catch(() => toast.error("Laden mislukt"));
    if (tb === "phases") api.get("/provocative/admin/phases", adminAuth()).then(({ data }) => {
      setPhases(data.items);
      setReportDrafts(Object.fromEntries(data.items.map((p) => [p.phase, p.report || ""])));
    }).catch(() => toast.error("Laden mislukt"));
  };

  // aparte admin-login: alleen geautoriseerde rollen komen binnen (server-side afgedwongen)
  const adminLogin = async () => {
    setAErr("");
    if (!aForm.email.trim() || !aForm.password) { setAErr("VUL E-MAIL EN WACHTWOORD IN"); return; }
    setABusy(true);
    try {
      const { data } = await api.post("/provocative/auth/admin-login", aForm);
      provSetToken(data.token, true);
      setHasToken(true);
      setDenied(false);
      const { data: acc } = await api.get("/provocative/admin/access", { headers: { Authorization: `Bearer ${data.token}` } });
      setRole(acc.role);
      loadTab("overview");
    } catch (err) {
      const s = err?.response?.status;
      setAErr(s === 403 ? "GEEN TOEGANG — GEEN BEHEERDERSROL" : s === 429 ? "TE VEEL POGINGEN — PROBEER LATER" : "ONGELDIGE INLOGGEGEVENS");
    }
    setABusy(false);
  };

  useEffect(() => {
    api.get("/provocative/admin/access", adminAuth())
      .then(({ data }) => { setRole(data.role); loadTab("overview"); })
      .catch(() => setDenied(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const goTab = (tb) => { setTab(tb); loadTab(tb); };

  const openRoster = (s) => {
    setOpen(s);
    setExpanded(null);
    setFilter("all");
    api.get(`/provocative/admin/roster/${s.id}`, adminAuth()).then(({ data }) => setRoster(data))
      .catch(() => toast.error("Laden mislukt"));
  };

  const togglePresent = async (userId, present) => {
    try {
      await api.post("/provocative/admin/attendance", { session_id: open.id, user_id: userId, present }, adminAuth());
      openRoster(open);
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const sessionAction = async (s, action, notify = false) => {
    try {
      await api.post(`/provocative/admin/sessions/${s.id}/status`, { action, notify }, adminAuth());
      toast.success("Bijgewerkt");
      loadSessions();
      if (open?.id === s.id) setOpen(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/provocative/admin/sessions/${edit.id}`, edit, adminAuth());
      toast.success(edit.apply_all ? "Opgeslagen — rooster en komende weken bijgewerkt" : "Opgeslagen");
      setEdit(null);
      loadSessions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Mislukt (HTTP ${err?.response?.status || "netwerk"})`);
    }
  };

  const saveNew = async () => {
    try {
      await api.post("/provocative/admin/sessions", adding, adminAuth());
      toast.success("Sessie aangemaakt");
      setAdding(false);
      loadSessions();
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const searchMembers = (q) => {
    setWq(q);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      api.get(`/provocative/admin/members/search?q=${encodeURIComponent(q)}`, adminAuth())
        .then(({ data }) => setWResults(data.items)).catch(() => {});
    }, 250);
  };

  const addWalkin = async (payload) => {
    try {
      await api.post("/provocative/admin/walkin", { session_id: open.id, present: true, ...payload }, adminAuth());
      toast.success("Walk-in toegevoegd");
      setWalkin(false);
      setWq("");
      setWResults([]);
      setWNew({ first_name: "", last_name: "", email: "" });
      openRoster(open);
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const resolveGear = async (reportId, item, status, inRoster = true) => {
    try {
      await api.post(`/provocative/admin/equipment/${reportId}/resolve`, { item, status }, adminAuth());
      if (inRoster && open) openRoster(open);
      else loadTab("equipment");
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const memberStatus = async (m, action) => {
    try {
      await api.post(`/provocative/admin/members/${m.id}/status`, { action }, adminAuth());
      toast.success(action === "approve" ? "Goedgekeurd" : "Afgewezen");
      loadTab("members");
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  // member verwijderen (incl. RSVP/aanwezigheid/equipment) — vraagt altijd om bevestiging
  const memberDelete = async (m) => {
    if (!window.confirm(`Weet je zeker dat je ${m.first_name} ${m.last_name} (${m.email}) wilt verwijderen? Dit verwijdert ook aanwezigheid, RSVP's en equipment-meldingen. / Are you sure?`)) return;
    try {
      await api.delete(`/provocative/admin/members/${m.id}`, adminAuth());
      toast.success("Member verwijderd");
      loadTab("members");
    } catch (err) { toast.error(err?.response?.data?.detail || "Verwijderen mislukt"); }
  };

  const teamAct = async (mid, action, body) => {
    try {
      await api.post(`/provocative/admin/team/${mid}/${action}`, body || {}, adminAuth());
      toast.success("Bijgewerkt");
      loadTab("team");
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const sendTeamInvite = async () => {
    if (!tInvite.email.trim()) { toast.error("E-mailadres is verplicht"); return; }
    try {
      await api.post("/provocative/admin/team/invite", tInvite, adminAuth());
      toast.success("Uitnodiging verstuurd");
      setTInvite({ email: "", role: "beheer" });
      loadTab("team");
    } catch (err) { toast.error(err?.response?.data?.detail || "Uitnodigen mislukt"); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.patch("/provocative/admin/settings", settings, adminAuth());
      toast.success("Instellingen opgeslagen");
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  // media vervangen via chunked upload (grote video's blijven onder de proxy-limiet)
  const uploadMedia = async (slot, file) => {
    const CHUNK = 3 * 1024 * 1024;
    setUpProg((p) => ({ ...p, [slot]: 0 }));
    try {
      const { data: s } = await api.post("/provocative/admin/media/upload/start",
        { slot, filename: file.name, size: file.size }, adminAuth());
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        await api.post(`/provocative/admin/media/upload/${s.upload_id}/chunk`, blob,
          { headers: { ...adminAuth().headers, "Content-Type": "application/octet-stream" } });
        setUpProg((p) => ({ ...p, [slot]: Math.round(((i + 1) / total) * 100) }));
      }
      toast.info("Verwerken (video wordt geoptimaliseerd)…");
      await api.post(`/provocative/admin/media/upload/${s.upload_id}/finish`, {}, adminAuth());
      toast.success("Media bijgewerkt");
      loadTab("media");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload mislukt");
    }
    setUpProg((p) => ({ ...p, [slot]: null }));
  };

  const mono = "font-mono text-[10px] tracking-[0.25em] uppercase";
  const inp = "w-full bg-transparent border-b border-[#333333] focus:border-[#BEBEBE] py-2.5 font-mono text-sm outline-none placeholder:text-[#4A4A4A] transition-colors";
  const card = { background: CARD, borderColor: PROV.border };

  if (denied) {
    // geen geldige sessie → aparte admin-login (alleen geautoriseerde rollen komen verder)
    if (!hasToken) {
      return (
        <div className="min-h-screen flex flex-col" style={{ background: ABG }} data-testid="prov-admin-login">
          <StickyNav>
            <LangSwitch lang={lang} setLang={setLang} />
          </StickyNav>
          <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-full max-w-sm">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-center" style={{ color: PROV.muted }} data-testid="prov-admin-login-title">PROVOCATIVE. ADMIN — SIGN IN</div>
            <div className="space-y-6 mt-8">
              <input value={aForm.email} onChange={(e) => setAForm({ ...aForm, email: e.target.value })}
                placeholder="EMAIL ADDRESS" type="email" autoComplete="email"
                className={`${inp}`} style={{ color: PROV.primary }} data-testid="prov-admin-login-email" />
              <input value={aForm.password} onChange={(e) => setAForm({ ...aForm, password: e.target.value })}
                placeholder="PASSWORD" type="password" autoComplete="current-password"
                className={`${inp}`} style={{ color: PROV.primary }} data-testid="prov-admin-login-password"
                onKeyDown={(e) => e.key === "Enter" && adminLogin()} />
            </div>
            {aErr && <p className="font-mono text-[11px] mt-6 text-[#FF6B5E]" data-testid="prov-admin-login-error">{aErr}</p>}
            <button onClick={adminLogin} disabled={aBusy}
              className="mt-8 w-full py-4 border font-mono text-xs tracking-[0.3em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
              style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-admin-login-submit">
              {aBusy ? "…" : "SIGN IN →"}
            </button>
            <button onClick={() => nav("/provocative")} className={`${mono} mt-8 hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.muted }} data-testid="prov-admin-back">← PROVOCATIVE.</button>
          </div>
          </div>
        </div>
      );
    }
    // wel sessie, maar geen beheerdersrol
    return (
      <div className="min-h-screen flex flex-col" style={{ background: ABG }} data-testid="prov-admin-denied">
        <StickyNav>
          <LangSwitch lang={lang} setLang={setLang} />
        </StickyNav>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className={mono} style={{ color: PROV.secondary }}>GEEN TOEGANG / NO ACCESS</p>
          <button onClick={() => setHasToken(false)} className={`${mono} mt-6 hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.highlight }} data-testid="prov-admin-relogin">ADMIN LOGIN →</button>
          <button onClick={() => nav("/provocative")} className={`${mono} mt-6 hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.muted }} data-testid="prov-admin-back">← PROVOCATIVE.</button>
        </div>
      </div>
    );
  }

  if (!role) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: ABG }} data-testid="prov-admin-loading"><span className={`${mono} text-[#666666]`}>PROVOCATIVE. ADMIN …</span></div>;
  }

  const TABS = [
    ["overview", "OVERVIEW"], ["members", "MEMBERS"], ["sessions", "SESSIONS"], ["attendance", "ATTENDANCE"],
    ...(!readOnly ? [["equipment", "EQUIPMENT"]] : []),
    ["team", "TEAM ACCESS"], ["media", "MEDIA"], ["settings", "SETTINGS"], ["phases", "PHASE GATE"],
  ];

  const PHASE_META = {
    1: "COMPANIES INFRASTRUCTURE",
    2: "MEMBER PLATFORM",
    3: "AGENDA · ATTENDANCE · EQUIPMENT",
    4: "BILLING · MEDIA",
  };

  const participants = (roster?.participants || []).filter((p) => {
    if (filter === "registered") return p.rsvp_status === "confirmed";
    if (filter === "present") return p.present;
    if (filter === "expected") return !p.present;
    if (filter === "gear") return (p.missing_items || []).length > 0;
    return true;
  });

  const today = new Date().toISOString().slice(0, 10);
  const shownMembers = (members || []).filter((m) => mFilter === "all" || m.status === mFilter);
  const activeTeam = (team?.team || []).filter((x) => x.status === "active");

  return (
    <div className="min-h-screen" style={{ background: ABG, color: PROV.primary }} data-testid="prov-admin">
      <header className="flex items-center justify-between px-6 md:px-12 pt-6">
        <LogoHeader compact />
        <div className="flex items-center gap-6">
          <span className={mono} style={{ color: PROV.muted }} data-testid="prov-admin-role">{role === "central" ? "JOH ADMIN" : role.toUpperCase()}</span>
          <LangSwitch lang={lang} setLang={setLang} />
          <button onClick={() => nav("/provocative")} className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.muted }} data-testid="prov-admin-exit">← SITE</button>
        </div>
      </header>

      {/* tabbladen — operationeel; GEEN financiële sectie (facturatie = centrale JOH Admin) */}
      <nav className="px-6 md:px-12 mt-8 flex gap-6 overflow-x-auto border-b pb-3" style={{ borderColor: PROV.border }} data-testid="prov-admin-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => goTab(k)}
            className={`${mono} whitespace-nowrap transition-colors ${tab === k ? "text-[#C1C1C1] border-b border-[#C1C1C1] pb-1" : "text-[#666666] hover:text-[#858585]"}`}
            data-testid={`prov-tab-${k}`}>
            {label}
          </button>
        ))}
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* ============ OVERVIEW ============ */}
        {tab === "overview" && !open && (
          <section data-testid="prov-sec-overview">
            <h1 className="font-display text-2xl uppercase">Overview</h1>
            {!overview && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {overview && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px border mt-6" style={{ borderColor: PROV.border, background: PROV.border }} data-testid="prov-ov-members">
                  {[["MEMBERS", overview.members.total], ["APPROVED", overview.members.approved || 0], ["PENDING", overview.members.pending || 0], ["PROVISIONAL", overview.members.provisional || 0]].map(([k, v]) => (
                    <div key={k} className="px-4 py-5 text-center" style={card} data-testid={`prov-ov-${k.toLowerCase()}`}>
                      <div className={mono} style={{ color: PROV.muted }}>{k}</div>
                      <div className="font-display text-3xl mt-1">{v}</div>
                    </div>
                  ))}
                </div>
                {overview.next_session && (
                  <div className="mt-4 border p-6" style={card} data-testid="prov-ov-next">
                    <div className={mono} style={{ color: PROV.muted }}>NEXT SESSION</div>
                    <div className="font-display text-xl uppercase mt-2">{fmtSessionDate(overview.next_session.date, wdIndex(overview.next_session.date), lang)}</div>
                    <div className="font-mono text-xs mt-1" style={{ color: PROV.secondary }}>{overview.next_session.start_time} — {overview.next_session.end_time} · {overview.next_session.location}</div>
                    <div className="flex gap-6 mt-3">
                      <span className={mono} style={{ color: PROV.secondary }} data-testid="prov-ov-rsvp">RSVP {overview.next_session.rsvp_count}</span>
                      <span className={mono} style={{ color: PROV.secondary }} data-testid="prov-ov-present">PRESENT {overview.next_session.present_count}</span>
                    </div>
                  </div>
                )}
                <div className={`${mono} mt-4`} style={{ color: PROV.muted }} data-testid="prov-ov-upcoming">{overview.upcoming_count} UPCOMING SESSIONS</div>
              </>
            )}
          </section>
        )}

        {/* ============ MEMBERS ============ */}
        {tab === "members" && !open && (
          <section data-testid="prov-sec-members">
            <h1 className="font-display text-2xl uppercase">Members</h1>
            <div className="flex gap-4 mt-5 flex-wrap" data-testid="prov-member-filters">
              {[["all", "ALL"], ["pending", "PENDING"], ["approved", "APPROVED"], ["provisional", "PROVISIONAL"], ["rejected", "REJECTED"]].map(([k, label]) => (
                <button key={k} onClick={() => setMFilter(k)}
                  className={`${mono} transition-colors ${mFilter === k ? "text-[#C1C1C1] border-b border-[#C1C1C1]" : "text-[#666666] hover:text-[#858585]"}`}
                  data-testid={`prov-mfilter-${k}`}>{label}</button>
              ))}
            </div>
            {!members && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {members && (
              <div className="mt-5 border divide-y" style={{ borderColor: PROV.border, divideColor: PROV.border }} data-testid="prov-member-list">
                {shownMembers.length === 0 && <div className="px-5 py-4 font-mono text-xs" style={{ color: PROV.muted }} data-testid="prov-members-empty">—</div>}
                {shownMembers.map((m) => (
                  <div key={m.id} className="px-5 py-3.5" style={{ background: CARD }} data-testid={`prov-member-${m.email}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-sm truncate">{m.first_name} {m.last_name}</span>
                      <span className="font-mono text-xs truncate" style={{ color: PROV.muted }}>{m.email}</span>
                      <span className={mono} style={{ color: MSTATUS[m.status]?.[1] || PROV.muted }} data-testid={`prov-member-status-${m.email}`}>{MSTATUS[m.status]?.[0] || m.status.toUpperCase()}</span>
                      <span className={`${mono} ml-auto`} style={{ color: PROV.muted }}>ATTENDED {m.attended_total}</span>
                    </div>
                    {!readOnly && (
                      <div className="flex gap-4 mt-2">
                        {m.status !== "approved" && (
                          <button onClick={() => memberStatus(m, "approve")} className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: "#7BAF7B" }} data-testid={`prov-member-approve-${m.email}`}>APPROVE</button>
                        )}
                        {m.status !== "rejected" && (
                          <button onClick={() => memberStatus(m, "reject")} className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: "#FF6B5E" }} data-testid={`prov-member-reject-${m.email}`}>REJECT</button>
                        )}
                        <button onClick={() => memberDelete(m)} className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: "#666666" }} data-testid={`prov-member-delete-${m.email}`}>DELETE</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ============ SESSIONS ============ */}
        {tab === "sessions" && !open && (
          <section data-testid="prov-sec-sessions">
            <div className="flex items-center justify-between">
              <h1 className="font-display text-2xl uppercase" data-testid="prov-admin-title">Session Management</h1>
              {!readOnly && (
                <button onClick={() => setAdding({ date: "", start_time: "18:30", end_time: "19:30", location: "", visibility: "public" })}
                  className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.highlight }} data-testid="prov-session-add">+ ONE-TIME SESSION</button>
              )}
            </div>
            {!sessions && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {sessions && (
              <div className="mt-6 border divide-y" style={{ borderColor: PROV.border, divideColor: PROV.border }} data-testid="prov-session-list">
                {sessions.filter((s) => s.date >= today).map((s) => (
                  <div key={s.id} className="px-5 py-4" style={{ background: CARD }} data-testid={`prov-session-${s.date}`}>
                    <button onClick={() => openRoster(s)} className="w-full text-left" data-testid={`prov-session-open-${s.date}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <span className="font-display uppercase">{fmtSessionDate(s.date, wdIndex(s.date), lang)}</span>
                        <span className="font-mono text-xs" style={{ color: PROV.secondary }}>{s.start_time} — {s.end_time}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        <span className={mono} style={{ color: s.status === "published" ? "#7BAF7B" : s.status === "cancelled" ? "#FF6B5E" : PROV.muted }} data-testid={`prov-session-status-${s.date}`}>{s.status.toUpperCase()}</span>
                        <span className={mono} style={{ color: PROV.muted }}>{s.visibility.toUpperCase()}</span>
                        <span className={mono} style={{ color: PROV.muted }}>RSVP {s.rsvp_count} · PRESENT {s.present_count}</span>
                      </div>
                    </button>
                    {!readOnly && (
                      <div className="flex gap-4 mt-3">
                        <button onClick={() => setEdit({ id: s.id, date: s.date, start_time: s.start_time, end_time: s.end_time, location: s.location, visibility: s.visibility, notify: false, apply_all: false })}
                          className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.secondary }} data-testid={`prov-session-edit-${s.date}`}>EDIT</button>
                        {s.status !== "published" && <button onClick={() => sessionAction(s, "publish")} className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: "#7BAF7B" }} data-testid={`prov-session-publish-${s.date}`}>PUBLISH</button>}
                        {s.status !== "cancelled" && <button onClick={() => sessionAction(s, "cancel", true)} className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: "#FF6B5E" }} data-testid={`prov-session-cancel-${s.date}`}>CANCEL</button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ============ ATTENDANCE ============ */}
        {tab === "attendance" && !open && (
          <section data-testid="prov-sec-attendance">
            <h1 className="font-display text-2xl uppercase">Attendance — Digital Check-in</h1>
            <p className={`${mono} mt-3`} style={{ color: PROV.muted }}>KIES EEN SESSIE VOOR DE PRESENTIELIJST</p>
            {!sessions && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {sessions && (
              <div className="mt-6 border divide-y" style={{ borderColor: PROV.border, divideColor: PROV.border }} data-testid="prov-att-list">
                {sessions.map((s) => (
                  <button key={s.id} onClick={() => openRoster(s)} className="w-full text-left px-5 py-4" style={{ background: CARD }} data-testid={`prov-att-open-${s.date}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="font-display uppercase">{fmtSessionDate(s.date, wdIndex(s.date), lang)}</span>
                      <span className="font-mono text-xs" style={{ color: PROV.secondary }}>{s.start_time} — {s.end_time}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      <span className={mono} style={{ color: s.date < today ? PROV.muted : s.status === "cancelled" ? "#FF6B5E" : "#7BAF7B" }}>{s.date < today ? "PAST" : s.status.toUpperCase()}</span>
                      <span className={mono} style={{ color: PROV.muted }}>RSVP {s.rsvp_count} · PRESENT {s.present_count}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ============ EQUIPMENT ============ */}
        {tab === "equipment" && !open && !readOnly && (
          <section data-testid="prov-sec-equipment">
            <h1 className="font-display text-2xl uppercase">Equipment — Missing Gear</h1>
            {!equipment && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {equipment && equipment.map((blk) => (
              <div key={blk.session.id} className="mt-6 border" style={card} data-testid={`prov-eq-session-${blk.session.date}`}>
                <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: PROV.border }}>
                  <span className="font-display uppercase">{fmtSessionDate(blk.session.date, wdIndex(blk.session.date), lang)}</span>
                  <span className="font-mono text-xs" style={{ color: PROV.secondary }}>{blk.session.start_time} — {blk.session.end_time}</span>
                </div>
                {blk.reports.length === 0 && <div className="px-5 py-4 font-mono text-xs" style={{ color: PROV.muted }} data-testid={`prov-eq-empty-${blk.session.date}`}>NO REPORTS</div>}
                {blk.reports.map((r) => (
                  <div key={r.report_id} className="px-5 py-4 border-t" style={{ borderColor: PROV.border }} data-testid={`prov-eq-report-${r.email}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-sm">{r.name || r.email}</span>
                      <span className="font-mono text-xs" style={{ color: PROV.muted }}>{r.email}</span>
                      {r.reported_at && <span className={`${mono} ml-auto`} style={{ color: PROV.muted }}>{String(r.reported_at).slice(0, 16).replace("T", " ")}</span>}
                    </div>
                    {r.items.length === 0 && <div className="font-mono text-xs mt-2" style={{ color: "#7BAF7B" }}>ALL EQUIPMENT CONFIRMED</div>}
                    {r.items.map((item) => {
                      const res = (r.resolutions || {})[item]?.status || "reported";
                      return (
                        <div key={item} className="mt-2 flex items-center gap-3 flex-wrap" data-testid={`prov-eq-item-${r.email}-${item}`}>
                          <span className="font-mono text-xs" style={{ color: PROV.secondary }}>{GEAR_LABELS[item] || item}</span>
                          {[["reported", "REPORTED"], ["arranged", "ARRANGED"], ["unresolved", "UNRESOLVED"]].map(([st, label]) => (
                            <button key={st} onClick={() => resolveGear(r.report_id, item, st, false)}
                              className={`${mono} transition-colors ${res === st ? "text-[#C1C1C1] border-b border-[#C1C1C1]" : "text-[#666666] hover:text-[#858585]"}`}
                              data-testid={`prov-eq-resolve-${r.email}-${item}-${st}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </section>
        )}

        {/* ============ TEAM ACCESS ============ */}
        {tab === "team" && !open && (
          <section data-testid="prov-sec-team">
            <h1 className="font-display text-2xl uppercase">Team Access</h1>
            <p className={`${mono} mt-3`} style={{ color: PROV.muted }}>ADMIN (VOLLEDIG) · BEHEER (OPERATIONEEL) · KIJKEN (ALLEEN LEZEN)</p>
            {!team && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {team && (
              <>
                <div className="mt-5 border divide-y" style={{ borderColor: PROV.border, divideColor: PROV.border }} data-testid="prov-team-list">
                  {activeTeam.length === 0 && <div className="px-5 py-4 font-mono text-xs" style={{ color: PROV.muted }}>—</div>}
                  {activeTeam.map((m) => (
                    <div key={m.id} className="px-5 py-3.5 flex items-center gap-3 flex-wrap" style={{ background: CARD }} data-testid={`prov-team-${m.email}`}>
                      <span className="font-mono text-sm truncate flex-1 min-w-0">{m.email}</span>
                      {canManageTeam ? (
                        <>
                          <select value={m.role} onChange={(e) => teamAct(m.id, "role", { role: e.target.value })}
                            className="bg-transparent border-b border-[#333333] font-mono text-xs py-1 outline-none" style={{ color: PROV.primary }} data-testid={`prov-team-role-${m.email}`}>
                            {[["admin", "ADMIN"], ["beheer", "BEHEER"], ["kijken", "KIJKEN"]].map(([k, v]) => <option key={k} value={k} className="bg-[#161616]">{v}</option>)}
                          </select>
                          <button onClick={() => teamAct(m.id, "revoke")} className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: "#FF6B5E" }} data-testid={`prov-team-revoke-${m.email}`}>REVOKE</button>
                        </>
                      ) : (
                        <span className={mono} style={{ color: PROV.muted }} data-testid={`prov-team-rolelabel-${m.email}`}>{m.role.toUpperCase()}</span>
                      )}
                    </div>
                  ))}
                </div>
                {(team.invites || []).length > 0 && (
                  <div className="mt-4 border divide-y" style={{ borderColor: PROV.border, divideColor: PROV.border }} data-testid="prov-invite-list">
                    {team.invites.map((i) => (
                      <div key={i.id} className="px-5 py-3 flex items-center gap-3" style={{ background: CARD }} data-testid={`prov-invite-${i.email}`}>
                        <span className="font-mono text-xs truncate flex-1" style={{ color: PROV.secondary }}>{i.email}</span>
                        <span className={mono} style={{ color: PROV.muted }}>{i.role.toUpperCase()} · INVITED</span>
                      </div>
                    ))}
                  </div>
                )}
                {canManageTeam && (
                  <div className="mt-6 border p-5" style={card} data-testid="prov-team-invite-form">
                    <div className={mono} style={{ color: PROV.muted }}>+ INVITE TEAM MEMBER</div>
                    <div className="flex gap-3 mt-4 flex-wrap">
                      <input value={tInvite.email} onChange={(e) => setTInvite({ ...tInvite, email: e.target.value })} placeholder="EMAIL ADDRESS" className={`${inp} flex-1 min-w-48`} style={{ color: PROV.primary }} data-testid="prov-invite-email" />
                      <select value={tInvite.role} onChange={(e) => setTInvite({ ...tInvite, role: e.target.value })}
                        className="bg-transparent border-b border-[#333333] font-mono text-xs py-2 outline-none" style={{ color: PROV.primary }} data-testid="prov-invite-role">
                        {[["admin", "ADMIN"], ["beheer", "BEHEER"], ["kijken", "KIJKEN"]].map(([k, v]) => <option key={k} value={k} className="bg-[#161616]">{v}</option>)}
                      </select>
                      <button onClick={sendTeamInvite} className="px-6 py-3 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A]" style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-invite-submit">INVITE</button>
                    </div>
                    <p className="font-mono text-[10px] mt-3" style={{ color: PROV.muted }}>Uitnodiging verloopt na 7 dagen · acceptatie via beveiligde link per e-mail.</p>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* ============ MEDIA (bekijken: alle rollen; vervangen: central/admin) ============ */}
        {tab === "media" && !open && (
          <section data-testid="prov-sec-media">
            <h1 className="font-display text-2xl uppercase">Media</h1>
            <p className={`${mono} mt-3`} style={{ color: PROV.muted }} data-testid="prov-media-note">
              {media?.editable ? "VERVANG LOGO, POSTER EN VIDEO'S — UPLOADS WORDEN WEB-GEOPTIMALISEERD" : "ALLEEN BEKIJKEN — VERVANGEN KAN DOOR CENTRAL/ADMIN"}
            </p>
            {!media && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {media && (
              <div className="mt-6 space-y-6">
                {[["logo", "OFFICIAL LOGO (PNG)", "image"], ["poster", "INTRO POSTER (JPG)", "image"],
                  ["intro_video", "INTRO VIDEO", "video"], ["confirm_video", "CONFIRMATION VIDEO (AFTER RSVP)", "video"]].map(([slot, label, kind]) => (
                  <div key={slot} className="border p-5" style={card} data-testid={`prov-media-${slot}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className={mono} style={{ color: PROV.muted }}>{label}</div>
                      {media.editable && upProg[slot] == null && (
                        <label className={`${mono} cursor-pointer hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.highlight }} data-testid={`prov-media-replace-${slot}`}>
                          REPLACE
                          <input type="file" accept={kind === "video" ? "video/*" : "image/png,image/jpeg,image/webp"} className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMedia(slot, f); e.target.value = ""; }}
                            data-testid={`prov-media-file-${slot}`} />
                        </label>
                      )}
                    </div>
                    {(media.meta || {})[slot] && (
                      <div className="font-mono text-[10px] mt-2" style={{ color: PROV.muted }} data-testid={`prov-media-meta-${slot}`}>
                        {media.meta[slot].filename} · {(media.meta[slot].size / (1024 * 1024)).toFixed(1)} MB · {String(media.meta[slot].updated_at || "").slice(0, 16).replace("T", " ")}
                      </div>
                    )}
                    {upProg[slot] != null && (
                      <div className="mt-3" data-testid={`prov-media-progress-${slot}`}>
                        <div className="h-1 w-full" style={{ background: PROV.border }}>
                          <div className="h-1 transition-all" style={{ background: PROV.highlight, width: `${upProg[slot]}%` }} />
                        </div>
                        <div className={`${mono} mt-1`} style={{ color: PROV.secondary }}>{upProg[slot]}%</div>
                      </div>
                    )}
                    {slot === "logo" && <img src={media.logo} alt="PROVOCATIVE." className="mt-4 w-full max-w-md object-contain" />}
                    {slot === "poster" && <img src={media.poster} alt="" className="mt-4 w-full max-w-md object-cover" />}
                    {slot === "intro_video" && (
                      <video key={media.intro_video.mp4} controls playsInline preload="metadata" className="mt-4 w-full border" style={{ borderColor: PROV.border }} data-testid="prov-media-intro-video">
                        <source src={media.intro_video.mp4} type="video/mp4" />
                        <source src={media.intro_video.webm} type="video/webm" />
                      </video>
                    )}
                    {slot === "confirm_video" && (
                      <video key={media.confirm_video.mp4} controls playsInline preload="metadata" className="mt-4 w-full border" style={{ borderColor: PROV.border }} data-testid="prov-media-confirm-video">
                        <source src={media.confirm_video.mp4} type="video/mp4" />
                        <source src={media.confirm_video.webm} type="video/webm" />
                      </video>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ============ SETTINGS ============ */}
        {tab === "settings" && !open && (
          <section data-testid="prov-sec-settings">
            <h1 className="font-display text-2xl uppercase">Settings</h1>
            {!settings && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {settings && (
              <div className="mt-6 border p-6" style={card} data-testid="prov-settings-form">
                <div className={mono} style={{ color: PROV.muted }}>TRAINING DAYS (recurring)</div>
                <div className="space-y-3 mt-4" data-testid="prov-settings-days">
                  {settings.days.map((d, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center" data-testid={`prov-day-${i}`}>
                      <select value={d.weekday} onChange={(e) => setSettings({ ...settings, days: settings.days.map((x, j) => j === i ? { ...x, weekday: Number(e.target.value) } : x) })}
                        disabled={readOnly} className="bg-transparent border-b border-[#333333] font-mono text-xs py-2 outline-none disabled:opacity-40" style={{ color: PROV.primary }} data-testid={`prov-day-${i}-weekday`}>
                        {WEEKDAYS_EN.map((w, wi) => <option key={w} value={wi} disabled={settings.days.some((x, j) => j !== i && x.weekday === wi)} className="bg-[#161616]">{w}</option>)}
                      </select>
                      <input type="time" value={d.start_time} onChange={(e) => setSettings({ ...settings, days: settings.days.map((x, j) => j === i ? { ...x, start_time: e.target.value } : x) })}
                        disabled={readOnly} className="bg-transparent border-b border-[#333333] font-mono text-xs py-2 outline-none disabled:opacity-40" style={{ color: PROV.primary }} data-testid={`prov-day-${i}-start`} />
                      <input type="time" value={d.end_time} onChange={(e) => setSettings({ ...settings, days: settings.days.map((x, j) => j === i ? { ...x, end_time: e.target.value } : x) })}
                        disabled={readOnly} className="bg-transparent border-b border-[#333333] font-mono text-xs py-2 outline-none disabled:opacity-40" style={{ color: PROV.primary }} data-testid={`prov-day-${i}-end`} />
                      {!readOnly && (
                        <button onClick={() => setSettings({ ...settings, days: settings.days.filter((_, j) => j !== i) })} disabled={settings.days.length <= 1}
                          className={`${mono} hover:text-[#C1C1C1] disabled:opacity-30 transition-colors px-1`} style={{ color: PROV.secondary }} data-testid={`prov-day-${i}-remove`}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                {!readOnly && (
                  <button onClick={() => {
                      const used = settings.days.map((d) => d.weekday);
                      const free = [0, 1, 2, 3, 4, 5, 6].find((w) => !used.includes(w));
                      if (free === undefined) return;
                      setSettings({ ...settings, days: [...settings.days, { weekday: free, start_time: "18:30", end_time: "19:30" }] });
                    }} disabled={settings.days.length >= 7}
                    className={`${mono} hover:text-[#C1C1C1] disabled:opacity-30 transition-colors mt-4`} style={{ color: PROV.highlight }} data-testid="prov-day-add">+ ADD DAY</button>
                )}
                <div className={`${mono} mt-8`} style={{ color: PROV.muted }}>LOCATION</div>
                <input value={settings.location} onChange={(e) => setSettings({ ...settings, location: e.target.value })} disabled={readOnly}
                  className={`${inp} mt-2 disabled:opacity-40`} style={{ color: PROV.primary }} data-testid="prov-settings-location" />
                <label className="flex items-center gap-3 mt-6 cursor-pointer">
                  <input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} disabled={readOnly}
                    className="w-4 h-4 accent-[#BEBEBE] disabled:opacity-40" data-testid="prov-settings-enabled" />
                  <span className={mono} style={{ color: PROV.secondary }}>PROJECT PAGE ACTIVE (/provocative)</span>
                </label>
                {!readOnly && (
                  <button onClick={saveSettings} disabled={busy}
                    className="mt-8 px-8 py-3 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
                    style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-settings-save">
                    {busy ? "…" : "SAVE SETTINGS"}
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        {/* ============ PHASE GATE: owner review + formele goedkeuring ============ */}
        {tab === "phases" && !open && (
          <section data-testid="prov-sec-phases">
            <h1 className="font-display text-2xl uppercase">Phase Gate — Owner Review</h1>
            <p className={`${mono} mt-3`} style={{ color: PROV.muted }}>
              DE EIGENAAR BEOORDEELT HET FASERAPPORT EN KEURT FORMEEL GOED VÓÓR DE VOLGENDE FASE · GOEDKEURING WORDT GELOGD MET TIMESTAMP
            </p>
            {!phases && <p className={`${mono} mt-6 text-[#666666]`}>…</p>}
            {phases && phases.map((p) => (
              <div key={p.phase} className="mt-6 border p-6" style={card} data-testid={`prov-phase-${p.phase}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="font-display uppercase" data-testid={`prov-phase-title-${p.phase}`}>PHASE 0{p.phase} — {PHASE_META[p.phase]}</span>
                  <span className={mono} style={{ color: p.status === "approved" ? "#7BAF7B" : "#D4A93A" }} data-testid={`prov-phase-status-${p.phase}`}>
                    {p.status === "approved" ? "APPROVED" : "PENDING REVIEW"}
                  </span>
                </div>
                {p.status === "approved" && (
                  <div className="font-mono text-[10px] mt-2" style={{ color: PROV.secondary }} data-testid={`prov-phase-approved-${p.phase}`}>
                    APPROVED BY {p.approved_by} · {String(p.approved_at || "").slice(0, 19).replace("T", " ")} UTC
                  </div>
                )}
                <div className={`${mono} mt-5`} style={{ color: PROV.muted }}>PHASE REPORT</div>
                {role === "central" ? (
                  <textarea value={reportDrafts[p.phase] ?? ""} onChange={(e) => setReportDrafts({ ...reportDrafts, [p.phase]: e.target.value })}
                    rows={6} placeholder="Faserapport: wat is gebouwd, testresultaten, openstaande punten…"
                    className="w-full mt-2 bg-transparent border p-3 font-mono text-xs outline-none focus:border-[#BEBEBE] transition-colors placeholder:text-[#4A4A4A]"
                    style={{ borderColor: PROV.border, color: PROV.primary }} data-testid={`prov-phase-report-${p.phase}`} />
                ) : (
                  <p className="mt-2 font-mono text-xs whitespace-pre-wrap" style={{ color: PROV.secondary }} data-testid={`prov-phase-report-ro-${p.phase}`}>
                    {p.report || "—"}
                  </p>
                )}
                {role === "central" && (
                  <div className="flex gap-4 mt-4 flex-wrap">
                    <button onClick={async () => {
                        try {
                          await api.post(`/provocative/admin/phases/${p.phase}/report`, { report: reportDrafts[p.phase] ?? "" }, adminAuth());
                          toast.success("Rapport opgeslagen");
                          loadTab("phases");
                        } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
                      }}
                      className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.secondary }} data-testid={`prov-phase-save-${p.phase}`}>SAVE REPORT</button>
                    {p.status !== "approved" && (
                      <button onClick={async () => {
                          try {
                            await api.post(`/provocative/admin/phases/${p.phase}/report`, { report: reportDrafts[p.phase] ?? "" }, adminAuth());
                            await api.post(`/provocative/admin/phases/${p.phase}/approve`, {}, adminAuth());
                            toast.success(`Fase 0${p.phase} formeel goedgekeurd`);
                            loadTab("phases");
                          } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
                        }}
                        className="px-6 py-3 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:bg-[#7BAF7B] hover:text-[#0A0A0A]"
                        style={{ borderColor: "#7BAF7B", color: "#7BAF7B" }} data-testid={`prov-phase-approve-${p.phase}`}>APPROVE PHASE ✓</button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        {/* ============ ROSTER (geopende sessie vanuit SESSIONS of ATTENDANCE) ============ */}
        {open && roster && (
          <>
            <button onClick={() => setOpen(null)} className={`${mono} hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.muted }} data-testid="prov-roster-back">← BACK</button>
            <h1 className="font-display text-2xl uppercase mt-4" data-testid="prov-roster-title">Presentielijst — {fmtSessionDate(open.date, wdIndex(open.date), lang)}</h1>
            <div className="font-mono text-xs mt-1" style={{ color: PROV.secondary }}>{open.start_time} — {open.end_time} · {open.location}</div>

            <div className="grid grid-cols-3 gap-px border mt-6" style={{ borderColor: PROV.border, background: PROV.border }} data-testid="prov-roster-stats">
              {[["REGISTERED", roster.stats.registered], ["PRESENT", roster.stats.present], ["MISSING GEAR", roster.stats.missing_gear]].map(([k, v]) => (
                <div key={k} className="px-4 py-4 text-center" style={card} data-testid={`prov-stat-${k.toLowerCase().replace(" ", "-")}`}>
                  <div className={mono} style={{ color: PROV.muted }}>{k}</div>
                  <div className="font-display text-3xl mt-1">{v}</div>
                </div>
              ))}
            </div>

            {!readOnly && Object.keys(roster.equipment_summary).length > 0 && (
              <div className="mt-4 border px-5 py-4" style={card} data-testid="prov-gear-summary">
                <div className={mono} style={{ color: PROV.muted }}>EQUIPMENT NEEDED</div>
                <div className="mt-2 flex gap-5 flex-wrap">
                  {Object.entries(roster.equipment_summary).map(([k, v]) => (
                    <span key={k} className="font-mono text-xs" style={{ color: PROV.secondary }} data-testid={`prov-gear-sum-${k}`}>{GEAR_LABELS[k]} × {v}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center gap-4 flex-wrap" data-testid="prov-roster-filters">
              {[["all", "ALL"], ["registered", "REGISTERED"], ["present", "PRESENT"], ["expected", "EXPECTED"], ["gear", "MISSING GEAR ONLY"]].map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)}
                  className={`${mono} transition-colors ${filter === k ? "text-[#C1C1C1] border-b border-[#C1C1C1]" : "text-[#666666] hover:text-[#858585]"}`}
                  data-testid={`prov-filter-${k}`}>
                  {label}
                </button>
              ))}
              {!readOnly && (
                <button onClick={() => setWalkin(true)} className={`${mono} ml-auto hover:text-[#C1C1C1] transition-colors`} style={{ color: PROV.highlight }} data-testid="prov-walkin-open">+ ADD WALK-IN</button>
              )}
            </div>

            <div className="mt-4 border divide-y" style={{ borderColor: PROV.border, divideColor: PROV.border }} data-testid="prov-roster-list">
              {participants.length === 0 && <div className="px-5 py-4 font-mono text-xs" style={{ color: PROV.muted }} data-testid="prov-roster-empty">—</div>}
              {participants.map((p) => (
                <div key={p.user_id} style={{ background: CARD }} data-testid={`prov-row-${p.email}`}>
                  <div className="px-5 py-3.5 flex items-center gap-3">
                    {/* Present-checkbox ALTIJD zichtbaar, buiten de expandable sectie */}
                    {readOnly ? (
                      <span className="w-4 h-4 border flex items-center justify-center shrink-0" style={{ borderColor: PROV.border }} data-testid={`prov-present-ro-${p.email}`}>
                        {p.present && <span className="w-2 h-2" style={{ background: PROV.highlight }} />}
                      </span>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); togglePresent(p.user_id, !p.present); }}
                        className="w-4 h-4 border flex items-center justify-center shrink-0 transition-colors"
                        style={{ borderColor: p.present ? PROV.highlight : PROV.border }} aria-label={`present ${p.name}`} data-testid={`prov-present-${p.email}`}>
                        {p.present && <span className="w-2 h-2" style={{ background: PROV.highlight }} />}
                      </button>
                    )}
                    <button onClick={() => setExpanded(expanded === p.user_id ? null : p.user_id)} className="flex-1 flex items-center gap-3 text-left min-w-0" data-testid={`prov-expand-${p.email}`}>
                      <span className="font-mono text-sm truncate" style={{ color: PROV.primary }}>{p.name}</span>
                      <span className={mono} style={{ color: p.rsvp_status === "confirmed" ? "#7BAF7B" : p.rsvp_status === "walkin" ? PROV.highlight : PROV.muted }}>
                        {p.rsvp_status === "confirmed" ? "RSVP" : p.rsvp_status === "walkin" ? "WALK-IN" : "EXPECTED"}
                      </span>
                      {!readOnly && (p.missing_items || []).length > 0 && <span className={mono} style={{ color: "#D4A93A" }} data-testid={`prov-gear-flag-${p.email}`}>GEAR!</span>}
                      <span className="ml-auto font-mono text-xs" style={{ color: PROV.muted }}>{expanded === p.user_id ? "▲" : "▼"}</span>
                    </button>
                  </div>
                  {expanded === p.user_id && (
                    <div className="px-5 pb-5 pt-1 grid md:grid-cols-2 gap-5" data-testid={`prov-detail-${p.email}`}>
                      <div>
                        <div className={mono} style={{ color: PROV.muted }}>MEMBER INFORMATION</div>
                        <div className="font-mono text-xs mt-2 space-y-1" style={{ color: PROV.secondary }}>
                          <div>{p.name}</div>
                          <div>{p.email}</div>
                          <div>ATTENDED TOTAL: {p.attended_total}</div>
                        </div>
                        <div className={`${mono} mt-4`} style={{ color: PROV.muted }}>SESSION INFORMATION</div>
                        <div className="font-mono text-xs mt-2 space-y-1" style={{ color: PROV.secondary }}>
                          <div>RSVP: {p.rsvp_status.toUpperCase()}{p.rsvp_at ? ` · ${p.rsvp_at.slice(0, 16).replace("T", " ")}` : ""}</div>
                          <div>PRESENT: {p.present ? "YES" : "NO"}</div>
                        </div>
                      </div>
                      {!readOnly && (
                        <div>
                          <div className={mono} style={{ color: PROV.muted }}>EQUIPMENT</div>
                          {(p.missing_items || []).length === 0 && (
                            <div className="font-mono text-xs mt-2" style={{ color: PROV.secondary }}>
                              {p.equipment_state === "confirmed_available" ? "CONFIRMED AVAILABLE" : "CHECKLIST NOT COMPLETED"}
                            </div>
                          )}
                          {(p.missing_items || []).map((item) => {
                            const res = (p.equipment_resolutions || {})[item]?.status || "reported";
                            return (
                              <div key={item} className="mt-2 flex items-center gap-3 flex-wrap" data-testid={`prov-gear-item-${p.email}-${item}`}>
                                <span className="font-mono text-xs" style={{ color: PROV.secondary }}>{GEAR_LABELS[item]}</span>
                                {[["reported", "REPORTED"], ["arranged", "ARRANGED"], ["unresolved", "UNRESOLVED"]].map(([st, label]) => (
                                  <button key={st} onClick={() => resolveGear(p.equipment_report_id, item, st)}
                                    className={`${mono} transition-colors ${res === st ? "text-[#C1C1C1] border-b border-[#C1C1C1]" : "text-[#666666] hover:text-[#858585]"}`}
                                    data-testid={`prov-gear-resolve-${p.email}-${item}-${st}`}>
                                    {label}
                                  </button>
                                ))}
                              </div>
                            );
                          })}
                          {p.equipment_reported_at && <div className="font-mono text-[10px] mt-2" style={{ color: PROV.muted }}>REPORTED: {p.equipment_reported_at.slice(0, 16).replace("T", " ")}</div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* sessie bewerken */}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="prov-edit-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setEdit(null)} />
          <div className="relative border p-6 w-full max-w-sm" style={{ background: CARD, borderColor: PROV.border }}>
            <div className={mono} style={{ color: PROV.muted }}>EDIT SESSION {edit.date}</div>
            <div className="space-y-4 mt-5">
              <div className="flex gap-3">
                <input type="time" value={edit.start_time} onChange={(e) => setEdit({ ...edit, start_time: e.target.value })} className={inp} style={{ color: PROV.primary }} data-testid="prov-edit-start" />
                <input type="time" value={edit.end_time} onChange={(e) => setEdit({ ...edit, end_time: e.target.value })} className={inp} style={{ color: PROV.primary }} data-testid="prov-edit-end" />
              </div>
              <input value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} placeholder="LOCATION" className={inp} style={{ color: PROV.primary }} data-testid="prov-edit-location" />
              <select value={edit.visibility} onChange={(e) => setEdit({ ...edit, visibility: e.target.value })} className={inp} style={{ color: PROV.primary, background: CARD }} data-testid="prov-edit-visibility">
                <option value="public">PUBLIC</option><option value="members">MEMBERS ONLY</option><option value="hidden">HIDDEN</option>
              </select>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={edit.apply_all} onChange={(e) => setEdit({ ...edit, apply_all: e.target.checked })} className="w-4 h-4 accent-[#BEBEBE]" data-testid="prov-edit-apply-all" />
                <span className={mono} style={{ color: PROV.secondary }}>APPLY TO ALL WEEKS (UPDATE SCHEDULE + FUTURE SESSIONS)</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={edit.notify} onChange={(e) => setEdit({ ...edit, notify: e.target.checked })} className="w-4 h-4 accent-[#BEBEBE]" data-testid="prov-edit-notify" />
                <span className={mono} style={{ color: PROV.secondary }}>NOTIFY CONFIRMED MEMBERS</span>
              </label>
            </div>
            <div className="flex gap-3 mt-7">
              <button onClick={saveEdit} className="flex-1 py-3 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A]" style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-edit-save">SAVE</button>
              <button onClick={() => setEdit(null)} className={mono} style={{ color: PROV.muted }} data-testid="prov-edit-cancel">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* eenmalige sessie */}
      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="prov-add-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setAdding(false)} />
          <div className="relative border p-6 w-full max-w-sm" style={{ background: CARD, borderColor: PROV.border }}>
            <div className={mono} style={{ color: PROV.muted }}>ONE-TIME SESSION</div>
            <div className="space-y-4 mt-5">
              <input type="date" value={adding.date} onChange={(e) => setAdding({ ...adding, date: e.target.value })} className={inp} style={{ color: PROV.primary }} data-testid="prov-add-date" />
              <div className="flex gap-3">
                <input type="time" value={adding.start_time} onChange={(e) => setAdding({ ...adding, start_time: e.target.value })} className={inp} style={{ color: PROV.primary }} data-testid="prov-add-start" />
                <input type="time" value={adding.end_time} onChange={(e) => setAdding({ ...adding, end_time: e.target.value })} className={inp} style={{ color: PROV.primary }} data-testid="prov-add-end" />
              </div>
              <input value={adding.location} onChange={(e) => setAdding({ ...adding, location: e.target.value })} placeholder="LOCATION (OPTIONAL)" className={inp} style={{ color: PROV.primary }} data-testid="prov-add-location" />
            </div>
            <div className="flex gap-3 mt-7">
              <button onClick={saveNew} className="flex-1 py-3 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A]" style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-add-save">CREATE</button>
              <button onClick={() => setAdding(false)} className={mono} style={{ color: PROV.muted }} data-testid="prov-add-cancel">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* walk-in */}
      {walkin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="prov-walkin-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setWalkin(false)} />
          <div className="relative border p-6 w-full max-w-sm" style={{ background: CARD, borderColor: PROV.border }}>
            <div className={mono} style={{ color: PROV.muted }}>ADD WALK-IN</div>
            <input value={wq} onChange={(e) => searchMembers(e.target.value)} placeholder="SEARCH NAME OR EMAIL" className={`${inp} mt-5`} style={{ color: PROV.primary }} data-testid="prov-walkin-search" />
            <div className="mt-2 max-h-40 overflow-y-auto divide-y" style={{ divideColor: PROV.border }} data-testid="prov-walkin-results">
              {wResults.map((m) => (
                <button key={m.user_id} onClick={() => addWalkin({ user_id: m.user_id })} className="w-full text-left py-2.5 font-mono text-xs hover:text-[#C1C1C1] transition-colors" style={{ color: PROV.secondary }} data-testid={`prov-walkin-pick-${m.email}`}>
                  {m.name} · {m.email} · {m.status.toUpperCase()}
                </button>
              ))}
            </div>
            <div className={`${mono} mt-6`} style={{ color: PROV.muted }}>OR NEW PARTICIPANT</div>
            <div className="space-y-3 mt-3">
              <input value={wNew.first_name} onChange={(e) => setWNew({ ...wNew, first_name: e.target.value })} placeholder="FIRST NAME" className={inp} style={{ color: PROV.primary }} data-testid="prov-walkin-first" />
              <input value={wNew.last_name} onChange={(e) => setWNew({ ...wNew, last_name: e.target.value })} placeholder="LAST NAME" className={inp} style={{ color: PROV.primary }} data-testid="prov-walkin-last" />
              <input value={wNew.email} onChange={(e) => setWNew({ ...wNew, email: e.target.value })} placeholder="EMAIL" className={inp} style={{ color: PROV.primary }} data-testid="prov-walkin-email" />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => addWalkin(wNew)} className="flex-1 py-3 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40" style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-walkin-save">ADD + PRESENT</button>
              <button onClick={() => setWalkin(false)} className={mono} style={{ color: PROV.muted }} data-testid="prov-walkin-cancel">CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
